/**
 * The flex bench. The homepage's signature move and its engineered peak.
 *
 * Flex rating is not a marketing number. It is the force, in pounds, required
 * to deflect a shaft one inch in a three-point bend test: supported near both
 * ends, loaded at the centre. Every hockey site on earth prints that number as
 * an abstract spec. This one runs the test.
 *
 * Scroll is the press. The shaft bends, passes its rating, and fails at the
 * centre, which is where the bending moment is greatest in this rig, so the
 * drawing tells the truth about its own geometry. Then it is repaired: the
 * halves come back together, new carbon draws over the break, and the shaft
 * reloads past the load that broke it.
 *
 * WHAT IS DRAWN, AND WHY IT IS A STICK
 *
 * The first version of this drew the specimen as a plain stroked beam. It was
 * honest and it was forgettable: a rod bending is a physics diagram, and it
 * could have been any rod. The whole argument depends on the visitor
 * recognising the object as the thing in their garage, so the specimen is now
 * built as a real stick — butt end, shaft, hosel, blade — as a filled outline
 * with a varying section, not a stroke.
 *
 * The overhangs are physics, not decoration. Only the span between the two
 * supports carries load, so the butt end and the hosel stay straight and pivot
 * about the slope of the beam where they leave it. That is why the butt end and
 * the blade kick upward as the middle is pressed down, which is exactly what a
 * real stick does in a real rig.
 *
 * HONESTY
 *
 * The deflection readout is arithmetic, not invention: at the rating,
 * deflection is one inch by definition, so inches = load / rating exactly.
 * The load figures are the demonstration's own and the panel says so. No claim
 * is made that a real shaft fails at a particular number, because carbon does
 * not.
 *
 * MECHANISM
 *
 * Bespoke page code driven off --sc-p, which the scrollcraft engine publishes
 * on the act element. The engine is not touched. Path data is recomputed per
 * frame, which is the one thing on this page that is not a transform or an
 * opacity — a bending beam cannot be drawn any other way — so it is confined to
 * three paths inside one SVG and runs only while the act is on screen.
 */
(() => {
  'use strict';

  const act = document.querySelector('[data-flex-bench]');
  if (!act) return;

  const svg = act.querySelector('[data-fb-svg]');
  const el = {
    shaftA: act.querySelector('[data-fb-shaft-a]'),
    shaftB: act.querySelector('[data-fb-shaft-b]'),
    blade: act.querySelector('[data-fb-blade]'),
    ghost: act.querySelector('[data-fb-ghost]'),
    sleeve: act.querySelector('[data-fb-sleeve]'),
    splinter: act.querySelector('[data-fb-splinter]'),
    flash: act.querySelector('[data-fb-flash]'),
    ram: act.querySelector('[data-fb-ram]'),
    head: act.querySelector('[data-fb-head]'),
    rail: act.querySelector('[data-fb-rail]'),
    supA: act.querySelector('[data-fb-support-a]'),
    supB: act.querySelector('[data-fb-support-b]'),
    stage: act.querySelector('[data-fb-stage]'),
    load: act.querySelector('[data-fb-load]'),
    pct: act.querySelector('[data-fb-pct]'),
    defl: act.querySelector('[data-fb-defl]'),
    state: act.querySelector('[data-fb-state]')
  };

  if (!svg || !el.shaftA) return;

  /* ---- the specimen on the bench ------------------------------------- */
  const RATING = parseInt(act.dataset.rating, 10) || 87; // a real flex in this catalogue
  const OVER = 124; // the demonstration's failure load, labelled as such
  const RELOAD = 131; // what the repaired shaft is taken to

  /* ---- the score ------------------------------------------------------ */
  const REST = 0.1; //  0.00 - 0.10  authored silence: whole stick, no load
  const SPEC = 0.42; //  0.10 - 0.42  load climbs to the rating
  const SNAP = 0.5; //  0.42 - 0.50  past spec, then it lets go
  const HOLD = 0.6; //  0.50 - 0.60  the broken stick holds
  const FIXED = 0.78; //  0.60 - 0.78  the halves rejoin, new carbon draws
  //                     0.78 - 1.00  reloaded, and it holds

  const N_SPAN = 30; // samples across the tested span
  const N_BLADE = 14; // samples along the blade

  // How far the blade turns off the shaft axis, and how it gets there. A real
  // blade turns most of its angle in the first third, at the heel, and then
  // runs close to straight out to the toe.
  const BLADE_SWEEP = (78 * Math.PI) / 180;
  const bladeEase = (t) => 1 - Math.pow(1 - t, 2.1);

  /** Section along the blade: out of the hosel it widens quickly to the heel,
   *  holds most of its depth along the face, and rounds off at the toe. */
  function bladeWidth(t) {
    if (t < 0.28) return lerp(g.w * 0.74, g.w * 2.05, t / 0.28);
    if (t < 0.78) return lerp(g.w * 2.05, g.w * 1.8, (t - 0.28) / 0.5);
    return lerp(g.w * 1.8, g.w * 0.55, (t - 0.78) / 0.22);
  }

  /* ---- geometry, set per layout --------------------------------------- */
  let g = {};
  let live = false;
  let last = -1;

  const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
  const lerp = (a, b, t) => a + (b - a) * t;
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  /** Normalised deflection of a simply supported beam under a central load.
   *  1 at the centre, 0 at both supports, symmetric. */
  function shape(u) {
    const x = u > 0.5 ? 1 - u : u;
    return x * (3 - 4 * x * x);
  }

  /** Its slope, so the unloaded overhangs leave the span at the right angle. */
  function slope(u) {
    return u > 0.5 ? -(3 - 12 * (1 - u) * (1 - u)) : 3 - 12 * u * u;
  }

  function layout() {
    const vertical = window.matchMedia('(max-width: 47.9375rem)').matches;

    // Local space is (a, n): a runs along the undeformed axis from the first
    // support, n is the deflection normal. Mapping to the viewBox is the only
    // place orientation is decided, so every shape below is written once.
    g = vertical
      ? { vertical: true, vb: '0 0 330 596', L: 360, ax: 100, ay: 40, px: 52, w: 13, butt: 54, hosel: 30, blade: 84 }
      : { vertical: false, vb: '0 0 1200 302', L: 700, ax: 96, ay: 84, px: 100, w: 17, butt: 112, hosel: 60, blade: 150 };

    svg.setAttribute('viewBox', g.vb);

    // Static furniture, placed once rather than per frame.
    place(el.supA, ...map(0, 0));
    place(el.supB, ...map(g.L, 0));
    if (el.rail) {
      const mid = map(g.L / 2, 0);
      const reach = g.vertical ? 116 : 250;
      el.rail.setAttribute(
        'd',
        g.vertical
          ? `M${g.ax - 60} ${mid[1] - reach}V${mid[1] + reach}`
          : `M${mid[0] - reach} ${g.ay - 62}H${mid[0] + reach}`
      );
    }

    // The ghost is the undeformed axis of the TESTED SPAN, and nothing else.
    // Drawing the whole undeformed stick left a second dashed blade hanging in
    // the frame with nothing to mean.
    const s0 = map(0, 0);
    const s1 = map(g.L, 0);
    el.ghost.setAttribute('d', `M${s0[0]} ${s0[1]}L${s1[0]} ${s1[1]}`);
    last = -1;
  }

  function place(node, x, y) {
    if (node) node.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)})`);
  }

  /** Local (along, normal) to viewBox (x, y). */
  function map(a, n) {
    return g.vertical ? [g.ax + n, g.ay + a] : [g.ax + a, g.ay + n];
  }

  /* ------------------------------------------------------------ the stick --
   * One centreline from the butt end to the blade toe, with a width at every
   * point. Three regions:
   *
   *   butt    straight, pivoting on the beam's slope where it leaves support A
   *   span    the bent beam itself, between the supports
   *   hosel   straight, pivoting on the slope at support B
   *   blade   continues from the hosel, curving away, widening then tapering
   */
  function centre(delta) {
    const pts = [];
    const w = [];
    const push = (a, n, width) => {
      const p = map(a, n);
      pts.push({ x: p[0], y: p[1] });
      w.push(width);
    };

    const k0 = (delta * slope(0)) / g.L; // dn/da at support A
    const k1 = (delta * slope(1)) / g.L; // dn/da at support B

    // Butt overhang, drawn back from support A along the beam's own slope.
    push(-g.butt, -g.butt * k0, g.w * 0.92);
    push(-g.butt * 0.5, -g.butt * 0.5 * k0, g.w * 0.97);

    // The tested span.
    for (let i = 0; i <= N_SPAN; i++) {
      const u = i / N_SPAN;
      push(u * g.L, delta * shape(u), g.w);
    }
    const breakIndex = pts.length - 1 - Math.round(N_SPAN / 2);

    // Hosel: straight, kicking up on the slope at support B, and narrowing.
    push(g.L + g.hosel * 0.5, k1 * g.hosel * 0.5, g.w * 0.86);
    const hoselEnd = { a: g.L + g.hosel, n: k1 * g.hosel };
    push(hoselEnd.a, hoselEnd.n, g.w * 0.74);
    const bladeStart = pts.length - 1;

    // Blade: swept away from the shaft axis, widening then tapering to the toe.
    // Built in the hosel's own frame so it rides the bend instead of hinging
    // off a fixed angle.
    const t0 = Math.atan2(k1, 1);
    let a = hoselEnd.a;
    let n = hoselEnd.n;
    const step = g.blade / N_BLADE;
    for (let i = 1; i <= N_BLADE; i++) {
      const s = i / N_BLADE;
      const ang = t0 + BLADE_SWEEP * bladeEase(s);
      a += Math.cos(ang) * step;
      n += Math.sin(ang) * step;
      // Widen to a paddle over the first half, then taper to a rounded toe.
      push(a, n, bladeWidth(s));
    }

    return { pts, w, breakIndex, bladeStart };
  }

  /** The stick after it lets go.
   *
   *  Neither half carries a bending moment any more, so both are straight:
   *  each one hinges on its own support and falls to meet the other at the
   *  fracture, and the pieces slide apart along the axis as the gap opens. The
   *  butt end stays in line with the left piece and the hosel and blade stay in
   *  line with the right, because they are still the same piece of carbon.
   */
  function broken(drop, gap) {
    const whole = centre(0);
    const bi = whole.breakIndex;
    const half = gap / 2;
    const k = drop / (g.L / 2); // slope of each fallen half

    const toXY = (a, n) => {
      const m = map(a, n);
      return { x: m[0], y: m[1] };
    };

    // Left piece: butt end, then a straight run from support A down to the
    // fracture. Above support A the overhang keeps the same line, so the butt
    // kicks up exactly as far as the fracture has fallen.
    const leftPts = [];
    const leftW = [];
    const nAt = (a) => a * k;
    leftPts.push(toXY(-g.butt - half, nAt(-g.butt)));
    leftW.push(g.w * 0.92);
    for (let i = 0; i <= N_SPAN / 2; i++) {
      const t = i / (N_SPAN / 2);
      const a = lerp(0, g.L / 2, t);
      leftPts.push(toXY(a - half, nAt(a)));
      leftW.push(g.w);
    }

    // Right piece: the mirrored straight run, then the hosel, then the blade
    // swept off the end in the hosel's own frame.
    const rightPts = [];
    const rightW = [];
    const nAtR = (a) => (g.L - a) * k;
    for (let i = 0; i <= N_SPAN / 2; i++) {
      const t = i / (N_SPAN / 2);
      const a = lerp(g.L / 2, g.L, t);
      rightPts.push(toXY(a + half, nAtR(a)));
      rightW.push(g.w);
    }
    const hoselSlope = -k;
    let ha = g.L;
    let hn = 0;
    for (const [f, wf] of [[0.5, 0.86], [1, 0.74]]) {
      ha = g.L + g.hosel * f;
      hn = hoselSlope * g.hosel * f;
      rightPts.push(toXY(ha + half, hn));
      rightW.push(g.w * wf);
    }

    const t0 = Math.atan2(hoselSlope, 1);
    let ba = ha;
    let bn = hn;
    const step = g.blade / N_BLADE;
    for (let i = 1; i <= N_BLADE; i++) {
      const sN = i / N_BLADE;
      const ang = t0 + BLADE_SWEEP * bladeEase(sN);
      ba += Math.cos(ang) * step;
      bn += Math.sin(ang) * step;
      rightPts.push(toXY(ba + half, bn));
      rightW.push(bladeWidth(sN));
    }

    return { pts: leftPts, w: leftW, right: rightPts, rightW, bi };
  }

  /* ------------------------------------------------------- path builders -- */

  function polyline(pts) {
    let d = '';
    for (let i = 0; i < pts.length; i++) {
      d += (i ? 'L' : 'M') + pts[i].x.toFixed(1) + ' ' + pts[i].y.toFixed(1);
    }
    return d;
  }

  /** A closed outline around a centreline, offset by half the local width.
   *  This is what makes the specimen an object with a section rather than a
   *  stroke of constant weight. */
  function outline(pts, w, i0, i1) {
    const a = Math.max(0, i0);
    const b = Math.min(pts.length - 1, i1);
    if (b - a < 1) return '';
    const up = [];
    const down = [];
    for (let i = a; i <= b; i++) {
      const p = pts[i];
      const prev = pts[Math.max(a, i - 1)];
      const next = pts[Math.min(b, i + 1)];
      let dx = next.x - prev.x;
      let dy = next.y - prev.y;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
      const h = (w[Math.min(i, w.length - 1)] || g.w) / 2;
      up.push({ x: p.x - dy * h, y: p.y + dx * h });
      down.push({ x: p.x + dy * h, y: p.y - dx * h });
    }
    down.reverse();
    return polyline(up) + polyline(down).replace('M', 'L') + 'Z';
  }

  function setText(node, s) {
    if (node && node.textContent !== s) node.textContent = s;
  }

  /* ------------------------------------------------------------- render -- */

  function render(p) {
    let load = 0;
    let phase = 'rest';
    let sleeveT = 0;
    let gap = 0;
    let snapped = false;
    let burst = 0;

    if (p < REST) {
      phase = 'rest';
    } else if (p < SPEC) {
      load = RATING * ease(clamp01((p - REST) / (SPEC - REST)));
      phase = p > SPEC - 0.04 ? 'spec' : 'load';
    } else if (p < SNAP) {
      load = lerp(RATING, OVER, clamp01((p - SPEC) / (SNAP - SPEC)));
      phase = 'over';
    } else if (p < HOLD) {
      snapped = true;
      phase = 'snap';
      const t = clamp01((p - SNAP) / (HOLD - SNAP));
      gap = 26 * ease(t);
      // The event. Splinters and a local flare at the fracture, spiking on the
      // frame it lets go and gone within a fifth of the hold. Local, not a
      // full-frame flash: this is energy at the break, not a page transition.
      burst = Math.max(0, 1 - t / 0.55);
    } else if (p < FIXED) {
      snapped = true;
      phase = 'fix';
      const t = ease(clamp01((p - HOLD) / (FIXED - HOLD)));
      gap = 26 * (1 - t);
      sleeveT = clamp01((t - 0.35) / 0.65);
    } else {
      load = RELOAD * ease(clamp01((p - FIXED) / (1 - FIXED)));
      sleeveT = 1;
      phase = p > 0.94 ? 'held' : 'reload';
    }

    // Deflection in inches is arithmetic: at the rating the shaft deflects
    // exactly one inch, so inches = load / rating.
    const inches = load / RATING;
    const delta = inches * g.px;

    if (snapped) {
      const relax = phase === 'fix' ? 1 - ease(clamp01((p - HOLD) / (FIXED - HOLD))) : 1;
      const drop = (OVER / RATING) * g.px * 0.6 * relax;
      const br = broken(drop, gap);
      el.shaftA.setAttribute('d', outline(br.pts, br.w, 0, br.pts.length - 1));
      el.shaftB.setAttribute('d', outline(br.right, br.rightW, 0, br.right.length - 1 - N_BLADE));
      el.blade.setAttribute('d', outline(br.right, br.rightW, br.right.length - 1 - N_BLADE - 1, br.right.length - 1));
      el.shaftB.style.opacity = '1';
      if (el.sleeve && sleeveT > 0) {
        // Across the joint, not along one half: the wrap is what is closing
        // the gap, so it has to span it.
        const bridge = br.pts.slice(-4).concat(br.right.slice(0, 4));
        el.sleeve.setAttribute('d', polyline(bridge));
      }
    } else {
      const c = centre(delta);
      el.shaftA.setAttribute('d', outline(c.pts, c.w, 0, c.bladeStart));
      el.shaftB.style.opacity = '0';
      el.blade.setAttribute('d', outline(c.pts, c.w, c.bladeStart - 1, c.pts.length - 1));
      if (el.sleeve) {
        const lo = c.breakIndex - Math.round(N_SPAN * 0.09);
        const hi = c.breakIndex + Math.round(N_SPAN * 0.09);
        el.sleeve.setAttribute('d', polyline(c.pts.slice(lo, hi + 1)));
      }
    }

    // The wrap is a stroke so it can draw itself on. Everything else is a fill.
    if (el.sleeve) {
      const len = g.L * 0.2;
      el.sleeve.style.opacity = sleeveT > 0 ? '1' : '0';
      el.sleeve.style.strokeWidth = String(g.w * 1.7);
      el.sleeve.style.strokeDasharray = String(len);
      el.sleeve.style.strokeDashoffset = String(len - len * sleeveT);
    }

    // The press. A ram that visibly extends from a fixed rail: without it the
    // arrow simply travelled with the shaft and no force was visible.
    const mid = snapped ? map(g.L / 2, 0) : map(g.L / 2, delta);
    if (el.head) place(el.head, mid[0], mid[1]);
    if (el.ram) {
      const railPos = g.vertical ? g.ax - 60 : g.ay - 62;
      el.ram.setAttribute(
        'd',
        g.vertical ? `M${railPos} ${mid[1]}H${mid[0]}` : `M${mid[0]} ${railPos}V${mid[1]}`
      );
      el.ram.style.opacity = load > 0.5 || snapped ? '1' : '0.4';
    }

    // The fracture.
    const frac = snapped ? map(g.L / 2, (OVER / RATING) * g.px * 0.6) : mid;
    if (el.splinter) {
      // A base while the break exists, spiking on the frame it lets go. Carbon
      // does not part along a clean face, and a square cut read as a machined
      // joint rather than as a failure.
      const base = snapped && sleeveT < 0.5 ? 0.42 : 0;
      const o = Math.max(base, burst);
      el.splinter.style.opacity = o > 0 ? String(o.toFixed(3)) : '0';
      if (o > 0) {
        const sc = (0.62 + burst * 0.75).toFixed(3);
        el.splinter.setAttribute(
          'transform',
          `translate(${frac[0].toFixed(1)} ${frac[1].toFixed(1)}) scale(${sc})`
        );
      }
    }
    if (el.flash) {
      el.flash.style.opacity = burst > 0 ? String(burst * 0.55) : '0';
      el.flash.setAttribute('cx', frac[0].toFixed(1));
      el.flash.setAttribute('cy', frac[1].toFixed(1));
      el.flash.setAttribute('r', String((g.w * 3 + burst * g.w * 5).toFixed(1)));
    }

    setText(el.load, snapped ? '0' : String(Math.round(load)));
    setText(el.pct, snapped ? '—' : Math.round((load / RATING) * 100) + '%');
    setText(el.defl, snapped ? '—' : inches.toFixed(2) + '″');

    const states = {
      rest: 'At rest',
      load: 'Loading',
      spec: 'At spec',
      over: 'Past spec',
      snap: 'Snapped. Unsellable.',
      fix: 'New carbon over the break',
      reload: 'Loading again',
      held: 'Holding past the load that broke it'
    };
    setText(el.state, states[phase]);

    if (act.dataset.fbPhase !== phase) act.dataset.fbPhase = phase;

    // The harness cannot infer the state of a fixed stage it did not author, so
    // publish the values that actually paint. Rounded, and never raw progress:
    // if progress moves while the composition does not, that is precisely the
    // dead scroll this attribute exists to expose.
    if (el.stage) {
      el.stage.setAttribute(
        'data-sc-verify-state',
        phase + ':' + Math.round(load) + ':' + Math.round(sleeveT * 100) + ':' + Math.round(gap)
      );
    }
  }

  /* ---- reduced motion -------------------------------------------------
   * A scroll-driven bend test with motion switched off is not a gentler
   * version of itself, it is a still of one arbitrary frame. So the act stops
   * pinning (see the stylesheet) and the same generator draws the four states
   * as a plate of figures, which is how a service manual would have shown it in
   * the first place. Nothing is lost and nothing looks broken.
   */
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function renderPlate() {
    const figs = act.querySelectorAll('[data-fb-fig]');
    if (!figs.length) return;

    const saved = g;
    g = { vertical: false, vb: '0 0 300 150', L: 176, ax: 26, ay: 52, px: 21, w: 5, butt: 28, hosel: 16, blade: 39 };

    figs.forEach((fig) => {
      const f = parseInt(fig.dataset.fbFig, 10);
      const a = fig.querySelector('[data-fb-fig-a]');
      const b = fig.querySelector('[data-fb-fig-b]');
      const bl = fig.querySelector('[data-fb-fig-blade]');
      const sl = fig.querySelector('[data-fb-fig-sleeve]');
      if (!a) return;

      if (f === 3) {
        const drop = (OVER / RATING) * g.px * 0.6;
        const br = broken(drop, 13);
        a.setAttribute('d', outline(br.pts, br.w, 0, br.pts.length - 1));
        if (b) {
          b.setAttribute('d', outline(br.right, br.rightW, 0, br.right.length - 1 - N_BLADE));
          b.style.opacity = '1';
        }
        if (bl) bl.setAttribute('d', outline(br.right, br.rightW, br.right.length - 2 - N_BLADE, br.right.length - 1));
        if (sl) sl.style.opacity = '0';
      } else {
        const loads = { 1: 0, 2: OVER, 4: RELOAD };
        const c = centre((loads[f] / RATING) * g.px);
        a.setAttribute('d', outline(c.pts, c.w, 0, c.bladeStart));
        if (b) b.style.opacity = '0';
        if (bl) bl.setAttribute('d', outline(c.pts, c.w, c.bladeStart - 1, c.pts.length - 1));
        if (sl) {
          const lo = c.breakIndex - 3;
          const hi = c.breakIndex + 3;
          sl.setAttribute('d', polyline(c.pts.slice(lo, hi + 1)));
          sl.style.strokeWidth = String(g.w * 1.7);
          sl.style.opacity = f === 4 ? '1' : '0';
        }
      }
    });

    g = saved;
  }

  /* ---- wiring --------------------------------------------------------- */

  const io = new IntersectionObserver(
    (entries) => {
      live = entries.some((e) => e.isIntersecting);
    },
    { rootMargin: '25% 0px' }
  );
  io.observe(act);

  function frame() {
    if (live && !reduced.matches) {
      // Read the engine's published progress straight off the inline style.
      // getComputedStyle here would cost a style recalculation every frame for
      // a value the engine has already written as a string.
      const raw = parseFloat(act.style.getPropertyValue('--sc-p'));
      const p = clamp01(Number.isNaN(raw) ? 0 : raw);
      if (Math.abs(p - last) > 0.0008) {
        render(p);
        last = p;
      }
    }
    requestAnimationFrame(frame);
  }

  function boot() {
    if (reduced.matches) {
      act.classList.add('brk--static');
      layout();
      renderPlate();
      return;
    }
    act.classList.remove('brk--static');
    layout();
    render(0);
  }

  let lastW = window.innerWidth;
  window.addEventListener(
    'resize',
    () => {
      // Ignore URL-bar-only height changes on phones.
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      boot();
    },
    { passive: true }
  );

  if (reduced.addEventListener) reduced.addEventListener('change', boot);

  boot();
  requestAnimationFrame(frame);
})();
